import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { Typography } from '@material-ui/core';
import NumberFormat from 'react-number-format';

const headStyles = makeStyles(theme => ({
    headFont: {
        fontSize: 13,
        fontWeight: 600
    },
    fontBold: {
        fontWeight: 600
    }
}));

/**
 * EnhancedTableHead
 * @param props
 * @return {*}
 * @constructor
 */
function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };
    const headClasses = headStyles();

    const headRows = [
        { id: 'shareOrderNo', numeric: false, disablePadding: false, label: "Tertip" },
        { id: 'shareGroupNo', numeric: false, disablePadding: false, label: "Grup" },
        { id: 'shareNominalValue', numeric: false, disablePadding: false, label: "İtibari Değer" },
        { id: 'shareAmount', numeric: false, disablePadding: false, label: "Pay Adedi" },
        { id: 'totalShareValue', numeric: false, disablePadding: false, label: "Pay Tutarı (TL)" },
        { id: 'shareSerialNo', numeric: false, disablePadding: false, label: "Sıra No" },
        { id: 'explanation', numeric: false, disablePadding: false, label: "Açıklama " },
        { id: 'hpksShareCreateDate', numeric: false, disablePadding: false, label: "Devralma Tarihi" },
        { id: 'address', numeric: false, disablePadding: false, label: "Adres" },
        { id: 'addressCity', numeric: false, disablePadding: false, label: "İl" },
        { id: 'addressCounty', numeric: false, disablePadding: false, label: "İlçe" },
        { id: 'addressCountry', numeric: false, disablePadding: false, label: "Ülke" },
    ];

    return (
        <TableHead>
            <TableRow>
                {headRows.map(row => (
                    <TableCell
                        key={row.id}
                        align={row.numeric ? 'right' : 'left'}
                        padding={row.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === row.id ? order : false}
                        className={headClasses.headFont}
                    >
                        <TableSortLabel
                            active={orderBy === row.id}
                            direction={order}
                            onClick={createSortHandler(row.id)}
                        >
                            {
                                row.secondLabel ?
                                    <div>
                                        {row.label}<br />
                                        <Typography classes={{ subtitle1: headClasses.fontBold }}
                                                    variant="subtitle1">{row.secondLabel}</Typography>
                                    </div>
                                    :
                                    <div>{row.label}</div>
                            }
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
};

const useStyles = makeStyles(theme => ({
    table: {
        minWidth: 750,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    fontSize13: {
        fontSize: 13
    },
    link: {
        color:'#aa3323'
    }
}));
/**
 * BalanceInfoTable
 * @param props
 * @return {*}
 * @constructor
 */
export default function BalanceInfoTable(props) {
    const classes = useStyles();
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('memberTooltip');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const rows = props.accInfoData;

    const heads = [
        { label: props.intl.formatMessage({ id: "ACCOUNT_INFO.TABLE.MEMBER" }), key: 'memberTooltip' },
        { label: props.intl.formatMessage({ id: "ACCOUNT_INFO.TABLE.ACCOUNT_NO" }), key: 'accountNo' },
        { label: props.intl.formatMessage({ id: "ACCOUNT_INFO.TABLE.OPEN_DATE" }), key: 'openDate' },
        { label: props.intl.formatMessage({ id: "ACCOUNT_INFO.TABLE.PARTNERSHIP_TYPE" }), key: 'partnerType' },
        { label: props.intl.formatMessage({ id: "ACCOUNT_INFO.TABLE.ACCOUNT_STATE" }), key: 'accountState' },
    ];

    /**
     * handleRequestSort
     * @param event
     * @param property
     */
    function handleRequestSort(event, property) {
        const isDesc = orderBy === property && order === 'desc';
        setOrder(isDesc ? 'asc' : 'desc');
        setOrderBy(property);
    }

    /**
     * handleChangePage
     * @param event
     * @param newPage
     */
    function handleChangePage(event, newPage) {
        setPage(newPage);
    }

    /**
     * handleChangeRowsPerPage
     * @param event
     */
    function handleChangeRowsPerPage(event) {
        setRowsPerPage(+event.target.value);
        setPage(0);
    }

    return (
        <div>
            <div className={classes.tableWrapper} id="accountTable">
                <Table
                    className={classes.table}
                    aria-labelledby="tableTitle"
                    size={'medium'}
                >
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={rows.length}
                        intl={props.intl}
                    />
                    <TableBody>
                        {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => {
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={'row' + index}
                                    >
                                        <TableCell align="left" key={"shareOrderNo_" + index}
                                                   id={"shareOrderNo_" + index}
                                                   className={classes.fontSize13}>{row.shareOrderNo}</TableCell>
                                        <TableCell align="left" key={"shareGroupNo_" + index}
                                                   id={"shareGroupNo_" + index}
                                                   className={classes.fontSize13}>{row.shareGroupNo}</TableCell>
                                        <TableCell align="left" key={"shareNominalValue_" + index}
                                                   id={"shareNominalValue_" + index} 
                                                   className={classes.fontSize13}>{row.shareNominalValue}</TableCell>
                                        <TableCell align="left" key={"shareAmount_" + index}
                                                   id={"shareAmount_" + index}
                                                   className={classes.fontSize13}>{row.shareAmount}</TableCell>
                                        <TableCell align="left" key={"totalShareValue_" + index}
                                                   id={"totalShareValue_" + index} 
                                                   className={classes.fontSize13}>{row.totalShareValue}</TableCell>
                                        <TableCell align="left" key={"shareSerialNo_" + index}
                                                   id={"shareSerialNo_" + index}
                                                   className={classes.fontSize13}>{row.shareSerialNo}</TableCell>
                                        <TableCell align="left" key={"explanation_" + index}
                                                   id={"explanation_" + index}
                                                   className={classes.fontSize13}>{row.explanation}</TableCell>
                                        <TableCell align="left" key={"hpksShareCreateDate_" + index}
                                                   id={"hpksShareCreateDate_" + index}
                                                   className={classes.fontSize13}>{row.hpksShareCreateDate}</TableCell>
                                        <TableCell align="left" key={"address_" + index}
                                                   id={"address_" + index}
                                                   className={classes.fontSize13}>{row.address}</TableCell>
                                        <TableCell align="left" key={"addressCity_" + index}
                                                   id={"addressCity_" + index}
                                                   className={classes.fontSize13}>{row.addressCity}</TableCell>
                                        <TableCell align="left" key={"addressCounty_" + index}
                                                   id={"addressCounty_" + index}
                                                   className={classes.fontSize13}>{row.addressCounty}</TableCell>
                                        <TableCell align="left" key={"addressCountry_" + index}
                                                   id={"addressCountry_" + index}
                                                   className={classes.fontSize13}>{row.addressCountry}</TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </div>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                backIconButtonText={props.intl.formatMessage({ id: "TABLE.PAGINATION.PREVIOUS" })}
                nextIconButtonText={props.intl.formatMessage({ id: "TABLE.PAGINATION.NEXT" })}
                labelRowsPerPage={props.intl.formatMessage({ id: "TABLE.PAGINATION.PER_PAGE" })}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </div>
    )
}